import {
	Environment,
	PerspectiveCamera,
	useAnimations,
	useGLTF,
} from "@react-three/drei";

import { Canvas, useThree } from "@react-three/fiber";
import React, {
	type Dispatch,
	type SetStateAction,
	Suspense,
	useCallback,
	useEffect,
	useImperativeHandle,
	useLayoutEffect,
	useRef,
	useState,
} from "react";

import * as THREE from "three";

// Import your custom logic helpers here
import {
	get_anims,
	get_random_battle,
	get_random_fin,
	get_stage_track,
	get_stance_tracks,
} from "./utils";

type Dispatcher<S> = Dispatch<SetStateAction<S>>;

type SceneProps = {
	finished: boolean;
	setFinished: Dispatcher<boolean>;
	onFinished: () => void;
	isEndgame?: boolean;
	loop?: boolean;
};

// 1. The Model & Logic Component
const SceneContent = React.forwardRef((props: SceneProps, ref) => {
	const group = useRef(null);

	const { scene, animations, cameras } = useGLTF(
		`${import.meta.env.VITE_APP_WEBSITE_URL}/heavy/Scene_02.glb`,
	);

	// R3F's useAnimations creates a mixer for us automatically
	const { actions, mixer } = useAnimations(animations, group);

	// State to track indices (replacing global let variables)
	const state = useRef({ stage_idx: 0, stance_idx: 0 });

	// Lock camera aspect to 16:9 regardless of canvas size
	const { camera } = useThree();
	useLayoutEffect(() => {
		(camera as THREE.PerspectiveCamera).aspect = 16 / 9;
		camera.updateProjectionMatrix();
	}, [camera]);

	// Setup Shadows
	useLayoutEffect(() => {
		scene.traverse((child) => {
			if ((child as THREE.Mesh).isMesh) {
				child.castShadow = true;
				child.receiveShadow = true;
			}
		});
	}, [scene]);

	const go_rest = useCallback(() => {
		mixer.stopAllAction();
	}, [mixer]);

	const go_endgame = useCallback(() => {
		state.current.stage_idx = get_random_fin();
		state.current.stance_idx = 2;
	}, []);

	const go_battle = useCallback(() => {
		state.current.stage_idx = get_random_battle();
		state.current.stance_idx = 0;
	}, []);

	const go_battle_mega = useCallback(() => {
		state.current.stage_idx = get_random_battle();
		state.current.stance_idx = 1;
	}, []);

	useEffect(() => {
		const handleFinished = () => {
			if (!props.finished) {
				props.setFinished(true);
				props.onFinished();
			}
		};
		mixer.addEventListener("finished", handleFinished);
		return () => mixer.removeEventListener("finished", handleFinished);
	}, [mixer, props]);

	const play_current = useCallback(() => {
		props.setFinished(false);
		mixer.stopAllAction();

		const setLoopMode = (action: THREE.AnimationAction) => {
			if (props.loop) {
				action.setLoop(THREE.LoopRepeat, Infinity);
				action.clampWhenFinished = false;
			} else if (props.isEndgame) {
				action.setLoop(THREE.LoopRepeat, 2);
				action.clampWhenFinished = true;
			} else {
				// trigger_anim: loop until the phase exits
				action.setLoop(THREE.LoopRepeat, Infinity);
				action.clampWhenFinished = false;
			}
		};

		const stance_tracks = [...get_stance_tracks(state.current.stance_idx)];
		const stance_anims = get_anims(animations, stance_tracks);

		stance_anims.forEach((clip) => {
			const action = actions[clip.name];
			if (action) {
				setLoopMode(action);
				action.play();
			}
		});

		const stage_tracks = get_stage_track(state.current.stage_idx);
		const stage_anim = get_anims(animations, [stage_tracks]);

		stage_anim.forEach((clip) => {
			const action = actions[clip.name];
			if (action) {
				setLoopMode(action);
				action.play();
			}
		});
	}, [
		mixer,
		animations,
		actions,
		props.setFinished,
		props.isEndgame,
		props.loop,
	]);

	// Auto-play on mount: endgame loops victory stance, trigger_anim plays battle mega
	useEffect(() => {
		if (props.isEndgame) {
			go_endgame();
		} else {
			go_battle_mega();
		}
		play_current();
	}, [go_battle_mega, go_endgame, play_current, props.isEndgame]);

	// Expose functions to the parent via Ref
	useImperativeHandle(ref, () => ({
		play: () => {
			play_current();
		},
		go_battle,
		go_battle_mega,
		go_endgame,
		go_rest,
	}));

	// Use the camera from the GLTF
	const gltfCamera = cameras[0];

	return (
		<group ref={group} dispose={null}>
			{/* Set the GLTF camera as default */}
			<PerspectiveCamera
				makeDefault
				position={gltfCamera.position}
				rotation={gltfCamera.rotation}
				fov={(gltfCamera as THREE.PerspectiveCamera).fov}
				near={(gltfCamera as THREE.PerspectiveCamera).near}
				far={(gltfCamera as THREE.PerspectiveCamera).far}
			/>
			<primitive object={scene} />
		</group>
	);
});

export interface SceneControllerProps {
	onPhaseTriggered?: () => void;
	onAnimationFinished?: () => void;
	teamAEnergy?: number; // 0-100
	teamBEnergy?: number; // 0-100
	isEndgame?: boolean;
	loop?: boolean;
}

export function SceneController({
	onPhaseTriggered,
	onAnimationFinished,
	teamAEnergy = 0,
	teamBEnergy = 0,
	isEndgame = false,
	loop = false,
}: SceneControllerProps) {
	const sceneRef = useRef(null);

	// track animation states
	const [finished, setFinished] = useState(true);

	// Use refs to track previous energy levels to detect increments.
	// Initialize to 0 so first render always detects an increment and auto-plays
	// (fixes trigger_anim stuck state when component mounts with energy already >= 100).
	const prevEnergyA = useRef(0);
	const prevEnergyB = useRef(0);

	// Trigger battle animation when energy reaches milestones or increments
	useEffect(() => {
		if (!sceneRef.current) return;

		if (isEndgame) return;

		const hasIncremented =
			teamAEnergy > prevEnergyA.current || teamBEnergy > prevEnergyB.current;

		if (hasIncremented && finished) {
			if (teamAEnergy >= 100 || teamBEnergy >= 100) {
				(sceneRef.current as any).go_battle_mega();
				onPhaseTriggered?.();
			} else {
				(sceneRef.current as any).go_battle();
			}
			(sceneRef.current as any).play();
		}

		prevEnergyA.current = teamAEnergy;
		prevEnergyB.current = teamBEnergy;
	}, [teamAEnergy, teamBEnergy, finished, onPhaseTriggered, isEndgame]);

	return (
		<div className="w-full h-full relative overflow-hidden bg-slate-950">
			<Canvas
				shadows
				dpr={window.devicePixelRatio}
				resize={{ offsetSize: true }}
				gl={{
					antialias: false,
					powerPreference: "low-power",
					alpha: true,
					toneMapping: THREE.ACESFilmicToneMapping,
					toneMappingExposure: 1,
				}}
				style={{
					background: "transparent",
				}}
			>
				<ambientLight intensity={0.6} color={0xffffff} />

				{/* Environment (Replaces EXRLoader) */}
				<Environment
					files={`${import.meta.env.VITE_APP_WEBSITE_URL}/heavy/venice_sunset_1k.exr`}
					background={false}
				/>

				<Suspense fallback={null}>
					<SceneContent
						ref={sceneRef}
						finished={finished}
						setFinished={setFinished}
						onFinished={() => onAnimationFinished?.()}
						isEndgame={isEndgame}
						loop={loop}
					/>
				</Suspense>
			</Canvas>
		</div>
	);
}
